import _get from 'lodash/get';
import {
  AppUserAvailableDocumentationsQueryVariables,
  Asset,
  Site,
  SiteArea,
} from 'graphql-common';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import { DocumentFilterFieldNames } from 'routes/Documents/utils/useDocumentFilterFields';
import { ScannerActionType } from 'routes/Scanner/enums';
import isErrorTimeoutError from '@lib/utils/isErrorTimeoutError';
import { logger } from '@lib/services/logger';
import { CodeType } from './getCodeType';
import { ParsedCodes } from './getParsedCodes';
import { HandleQrCodeSuccessProps } from './types';

const defaultVariables: AppUserAvailableDocumentationsQueryVariables = {
  page: 1,
  limit: 1,
  documentationFilters: undefined,
  siteFindBy: { code: undefined },
  assetFindBy: { code: undefined },
  siteAreaFindBy: { code: undefined },
};

export default async function handleValidCodeForDocumentsAction(
  data: string,
  {
    fetchAppDocumentations,
    setLoading,
    setErrorStatus,
    onSuccessScanAction,
  }: HandleQrCodeSuccessProps,
  parsedCodes: ParsedCodes,
  codeType: CodeType,
) {
  try {
    const { entityCode, sideCode } = parsedCodes;

    const variables: AppUserAvailableDocumentationsQueryVariables = {
      ...defaultVariables,
      siteFindBy: { code: undefined },
      assetFindBy: { code: undefined },
      siteAreaFindBy: { code: undefined },
    };

    if (codeType !== CodeType.Invalid) {
      if (sideCode) {
        variables.siteFindBy = { code: sideCode };
      }
      if (entityCode) {
        variables.siteAreaFindBy = { code: entityCode };
        variables.assetFindBy = { code: entityCode };
      }
    }

    logger.debug('Scanner: find documents - querying API', { codeType });

    setLoading(true);
    const res = await fetchAppDocumentations({
      variables,
    });
    const { data: userAvailableData, error } = res;
    const isTimeoutError = isErrorTimeoutError(error);
    if (isTimeoutError) {
      logger.warn('Scanner: find documents - API timeout');
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailedOffline);
      return;
    }
    if (error) {
      logger.error('Scanner: find documents - API error', {
        error: error.message,
      });
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
      return;
    }

    const site = _get(userAvailableData, 'site') as Site;
    const siteArea = _get(userAvailableData, 'siteArea') as SiteArea;
    const asset = _get(userAvailableData, 'asset') as Asset;

    logger.debug('Scanner: find documents - API response', {
      hasSite: !!site,
      hasSiteArea: !!siteArea,
      hasAsset: !!asset,
      codeType,
    });

    setLoading(false);

    if (codeType === CodeType.Asset && !asset) {
      logger.warn(
        'Scanner: find documents - asset not found for Asset code type',
      );
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
      return;
    }

    if (codeType === CodeType.SiteArea && !siteArea) {
      logger.warn(
        'Scanner: find documents - site area not found for SiteArea code type',
      );
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
      return;
    }

    if ((sideCode || entityCode) && (site || siteArea || asset)) {
      // When we're prefilling Areas, skip Site — an area already implies
      // its parent site, so adding the site filter is redundant noise.
      const willPrefillArea = !!(
        entityCode &&
        siteArea &&
        codeType === CodeType.SiteArea
      );
      const values = {
        [DocumentFilterFieldNames.Sites]:
          sideCode && site && !willPrefillArea
            ? [{ value: site.id, label: site.name }]
            : undefined,
        [DocumentFilterFieldNames.Areas]: willPrefillArea
          ? [{ value: siteArea.id, label: siteArea.name }]
          : undefined,
        [DocumentFilterFieldNames.Equipment]:
          entityCode && asset && codeType === CodeType.Asset
            ? [{ value: asset.id, label: asset.name }]
            : undefined,
      };
      logger.info('Scanner: find documents - navigating to list with filters', {
        siteId: site?.id,
        siteName: site?.name,
        siteAreaId: siteArea?.id,
        siteAreaName: siteArea?.name,
        assetId: asset?.id,
        assetName: asset?.name,
      });
      onSuccessScanAction(values, ScannerActionType.FindDocuments);
    } else {
      logger.warn(
        'Scanner: find documents - no matching documents or entities found',
      );
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
    }
  } catch (error) {
    logger.error('Scanner: find documents - unexpected error', {
      error: error instanceof Error ? error.message : String(error),
    });
    setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
  }
}
